import { concatStyles } from '@styles/domain';
import {
  Fragment,
  type KeyboardEvent,
  type ReactElement,
  useState,
} from 'react';
import type { Choice } from './domain';
import * as styles from './index.css';

export interface ItemProps {
  role: 'option';
  tabIndex: number;
  'aria-selected': boolean;
  onClick: () => void;
  onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
}

interface Base<T> {
  choices: Choice<T>[];
  className?: string;
  renderChoice: (args: {
    choice: Choice<T>;
    selected: boolean;
    itemProps: ItemProps;
  }) => ReactElement;
}

interface Single<T> {
  value?: T;
  multiple?: never;
  onSelect: (value: T) => void;
}

interface Multiple<T> {
  values?: T[];
  multiple: true;
  standaloneValues?: T[];
  onSelect: (values: T[]) => void;
}

type Props<T> = Base<T> & (Single<T> | Multiple<T>);

const toggleMultiple = <T,>(
  current: T[],
  value: T,
  standaloneValues: T[],
): T[] => {
  const standaloneSet = new Set(standaloneValues);
  if (standaloneSet.has(value)) {
    return current.includes(value) ? [] : [value];
  }
  const withoutStandalone = current.filter((item) => !standaloneSet.has(item));
  return withoutStandalone.includes(value)
    ? withoutStandalone.filter((item) => item !== value)
    : [...withoutStandalone, value];
};

const ChoiceGroup = <T,>(props: Props<T>) => {
  const { choices, className, renderChoice } = props;
  const multiple = props.multiple === true;
  const isSelected = (choiceValue: T) =>
    props.multiple === true
      ? (props.values?.includes(choiceValue) ?? false)
      : props.value === choiceValue;
  const selectedIndex = choices.findIndex((choice) => isSelected(choice.value));
  const [activeIndex, setActiveIndex] = useState(
    selectedIndex >= 0 ? selectedIndex : 0,
  );

  const select = (choice: Choice<T>) => {
    if (props.multiple === true) {
      props.onSelect(
        toggleMultiple(
          props.values ?? [],
          choice.value,
          props.standaloneValues ?? [],
        ),
      );
      return;
    }
    props.onSelect(choice.value);
  };

  const focusOption = (origin: HTMLElement, index: number) => {
    const options = origin
      .closest('[role="listbox"]')
      ?.querySelectorAll<HTMLElement>('[role="option"]');
    if (!options?.length) {
      return;
    }
    const next = (index + options.length) % options.length;
    setActiveIndex(next);
    options[next].focus();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    // Ne pas capter le clavier quand on saisit dans l'input enfant (« Précisez »).
    if ((event.target as HTMLElement).tagName === 'INPUT') {
      return;
    }
    const origin = event.currentTarget;
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        event.preventDefault();
        focusOption(origin, activeIndex + 1);
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        event.preventDefault();
        focusOption(origin, activeIndex - 1);
        break;
      case 'Home':
        event.preventDefault();
        focusOption(origin, 0);
        break;
      case 'End':
        event.preventDefault();
        focusOption(origin, choices.length - 1);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        select(choices[activeIndex]);
        break;
    }
  };

  const handleClick = (index: number, choice: Choice<T>) => {
    setActiveIndex(index);
    select(choice);
  };

  return (
    <div
      role='listbox'
      aria-multiselectable={multiple}
      className={concatStyles([styles.list, className])}
    >
      {choices.map((choice, index) => {
        const selected = isSelected(choice.value);
        const itemProps: ItemProps = {
          role: 'option',
          tabIndex: index === activeIndex ? 0 : -1,
          'aria-selected': selected,
          onClick: () => handleClick(index, choice),
          onKeyDown: handleKeyDown,
        };
        return (
          <Fragment key={String(choice.value)}>
            {renderChoice({ choice, selected, itemProps })}
          </Fragment>
        );
      })}
    </div>
  );
};

export default ChoiceGroup;
